import { useCallback, useEffect, useMemo, useState } from "react";
import type { FormEvent } from "react";
import type { BoardSnapshot, ConversationRule } from "@business-ops/domain";
import {
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  LoaderCircle,
  MessageCircleMore,
  RefreshCw,
  Search,
  ShieldCheck,
  Smartphone,
  UsersRound,
  WifiOff
} from "lucide-react";
import {
  api,
  type WhatsAppCaptureSummary,
  type WhatsAppConnection,
  type WhatsAppConversationRule,
  type WhatsAppPerson,
  type WhatsAppSearchResult
} from "../api.js";
import { memberLabel } from "../helpers.js";
import { SafeLinkedText } from "../components/safe-linked-text.js";

export function WhatsAppConnectionView({ notify }: {
  notify: (text: string, tone?: "success" | "error") => void;
}) {
  const [connection, setConnection] = useState<WhatsAppConnection | null>(null);
  const [connectionUnavailable, setConnectionUnavailable] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  const [capture, setCapture] = useState<WhatsAppCaptureSummary | null>(null);

  const load = useCallback(async (): Promise<WhatsAppConnection | null> => {
    try {
      const next = await api.whatsAppConnection();
      setConnection(next);
      setConnectionUnavailable(false);
      return next;
    } catch {
      setConnection(null);
      setConnectionUnavailable(true);
      return null;
    }
  }, []);

  useEffect(() => {
    let active = true;
    let timer: number | undefined;
    const poll = async () => {
      const next = await load();
      if (!active) return;
      const delay = next?.status === "CONNECTED" ? 10_000 : 2_000;
      timer = window.setTimeout(() => void poll(), delay);
    };
    void poll();
    return () => {
      active = false;
      if (timer) window.clearTimeout(timer);
    };
  }, [load]);

  useEffect(() => {
    if (connection?.status !== "CONNECTED") {
      setCapture(null);
      return;
    }
    let active = true;
    const refresh = async () => {
      try {
        const next = await api.whatsAppCaptureSummary();
        if (active) setCapture(next);
      } catch {
        // Connection state remains useful when the optional count projection is
        // temporarily unavailable; the next quiet poll retries automatically.
      }
    };
    void refresh();
    const timer = window.setInterval(() => void refresh(), 10_000);
    return () => { active = false; window.clearInterval(timer); };
  }, [connection?.status]);

  const reconnect = async () => {
    setReconnecting(true);
    try {
      await api.reconnectWhatsApp();
      notify("Reconnecting WhatsApp.");
      window.setTimeout(() => void load(), 800);
    } catch (error) {
      notify(error instanceof Error ? error.message : "WhatsApp could not reconnect.", "error");
    } finally {
      setReconnecting(false);
    }
  };

  // WHY [BOL-WA-INITIAL-STATUS-001]: null initially means “not checked yet,”
  // not “connector offline.” The old shortcut showed a false outage during
  // every normal authenticated page load. Only a completed failed request may
  // render UNAVAILABLE; the falsifier is a healthy reload flashing offline.
  const status = connectionStatusForView(connection, connectionUnavailable);
  const state = connectionState(status);
  const StatusIcon = state.icon;

  return (
    <div className="view-stack connection-view">
      {status === "CONNECTED" && connection ? (
        <ConnectedAccountSummary connection={connection} capture={capture} />
      ) : (
        <section className={`connection-card connection-card--${state.tone}`}>
          <header className="connection-card__header">
            <span className="connection-status-icon"><StatusIcon className={state.spinning ? "spin" : ""} size={25} /></span>
            <div>
              <span className="connection-status-label">{state.label}</span>
              <h1>{connection?.botName ?? "Business Ops Manager"}</h1>
            </div>
            <span className="connection-live-mark"><i className="connection-dot" />{state.shortLabel}</span>
          </header>

          <div className="connection-card__body">
            {status === "SCAN_REQUIRED" && connection?.qrDataUrl ? (
              <div className="connection-scan-grid">
                <div className="qr-frame">
                  <img src={connection.qrDataUrl} alt="QR code for linking the Business Ops Manager WhatsApp account" />
                  <span>Refreshes automatically</span>
                </div>
                <div className="connection-instructions">
                  <p className="eyebrow">Use the business phone</p>
                  <h3>Scan this code in WhatsApp.</h3>
                  <ol>
                    <li><span>1</span><p>Open <strong>WhatsApp</strong> on the business phone.</p></li>
                    <li><span>2</span><p>Open <strong>Linked devices</strong>.</p></li>
                    <li><span>3</span><p>Tap <strong>Link a device</strong>, then scan this code.</p></li>
                  </ol>
                </div>
              </div>
            ) : (
              <div className="connection-message">
                <StatusIcon className={state.spinning ? "spin" : ""} size={38} />
                <h3>{state.heading}</h3>
                <p>{state.copy}</p>
                {(status === "DISCONNECTED" || status === "CONNECTING" || status === "UNAVAILABLE") ? (
                  <button className="primary-button" type="button" onClick={() => void reconnect()} disabled={reconnecting || !connection?.canReconnect}>
                    {reconnecting ? <><LoaderCircle className="spin" size={18} />Reconnecting…</> : <><RefreshCw size={18} />Reconnect</>}
                  </button>
                ) : null}
              </div>
            )}
          </div>

          <footer className="connection-card__footer">
            <span><ShieldCheck size={17} />Only owners and managers can open this page. The QR code is never saved.</span>
            <span className="group-check">
              <MessageCircleMore size={17} />
              Connect the business number
            </span>
          </footer>
        </section>
      )}

      {/* WHY [BOL-WA-ZERO-SETUP-UI-001]: the connected account is already the
          source of truth. High-confidence client groups are scoped by the
          server and uncertain chats fail closed; showing a 59-chat permission
          form made the manager rebuild WhatsApp by hand and contradicted the
          automatic workflow. Recovery policy APIs remain server-side, but the
          routine owner path is connection status plus optional search only. */}

      {status === "CONNECTED" ? <WhatsAppSearchPanel /> : null}

      {/* WHY [BOL-LIVE-DIRECTORY-UI-001]: pairing already exposes the channel's
          live directory and membership graph to the connector. Routine human
          matching would create a stale second address book and put dangerous
          routing work back on the manager. The adapter resolves exact unique
          identities continuously; ambiguity appears only inside the affected
          task with Send inert. Future Instagram/Threads adapters follow this
          same contract rather than adding another setup spreadsheet. */}
    </div>
  );
}

function WhatsAppSearchPanel() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<WhatsAppSearchResult[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const value = query.trim();
    if (value.length < 2) return;
    setLoading(true);
    setError("");
    try {
      const payload = await api.searchWhatsApp(value);
      setResults(payload.results);
    } catch (caught) {
      setResults(null);
      setError(caught instanceof Error ? caught.message : "Captured messages could not be searched.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <details className="whatsapp-search-panel">
      <summary>
        <span><Search size={19} /></span>
        <div><strong>Search messages</strong><small>Find captured text and voice transcripts.</small></div>
        <ChevronDown className="disclosure-chevron" size={18} />
      </summary>
      <div className="whatsapp-search-panel__body">
        <form className="whatsapp-search-form" onSubmit={(event) => void submit(event)}>
          <label><span className="sr-only">Search captured WhatsApp messages</span><input value={query} maxLength={120} onChange={(event) => setQuery(event.target.value)} placeholder="Search a client, task, or message" /></label>
          <button className="primary-button" type="submit" disabled={loading || query.trim().length < 2}>{loading ? <LoaderCircle className="spin" size={17} /> : <Search size={17} />}Search</button>
        </form>
        {error ? <p className="whatsapp-search-error">{error}</p> : null}
        {results?.length === 0 ? <p className="whatsapp-search-empty">No captured messages match that search.</p> : null}
        {results && results.length > 0 ? <ol className="whatsapp-search-results">{results.map((result) => <li key={result.sourceEventId}><header><strong>{result.conversationLabel}</strong><span>{result.senderLabel} · {formatSearchTime(result.occurredAt)}</span></header><p><SafeLinkedText text={result.content} /></p></li>)}</ol> : null}
      </div>
    </details>
  );
}

type ConversationRuleDraft = { rule: ConversationRule; clientId: string | null };

function ConversationRulesPanel({ clients, notify }: {
  clients: BoardSnapshot["clients"];
  notify: (text: string, tone?: "success" | "error") => void;
}) {
  const [conversations, setConversations] = useState<WhatsAppConversationRule[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, ConversationRuleDraft>>({});
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await api.whatsAppConversations();
      setConversations(payload.conversations);
      setDrafts({});
      setError("");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Conversation rules could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const save = async (conversation: WhatsAppConversationRule, draft: ConversationRuleDraft) => {
    setSavingId(conversation.conversationId);
    try {
      const payload = await api.updateWhatsAppConversationRule(conversation.conversationId, {
        expectedVersion: conversation.policyVersion,
        rule: draft.rule,
        clientId: draft.rule === "SCOPED_TEAM_REVIEW" ? draft.clientId : null
      });
      setConversations((current) => current?.map((row) => row.conversationId === conversation.conversationId
        ? payload.conversation
        : row) ?? [payload.conversation]);
      setDrafts((current) => {
        const next = { ...current };
        delete next[conversation.conversationId];
        return next;
      });
      setError("");
      notify("Review access saved.");
    } catch (caught) {
      notify(caught instanceof Error ? caught.message : "That conversation rule was not saved.", "error");
      await load();
    } finally {
      setSavingId(null);
    }
  };

  const enabledConversations = conversations?.filter((conversation) => conversation.rule !== "READ_ONLY") ?? [];
  const editableConversations = conversations?.filter((conversation) => conversation.editable) ?? [];
  const selectedConversation = editableConversations.find((conversation) => conversation.conversationId === selectedId) ?? null;
  const selectedDraft = selectedConversation
    ? drafts[selectedConversation.conversationId] ?? {
        rule: selectedConversation.rule,
        clientId: selectedConversation.clientId
      }
    : null;
  const selectedChanged = Boolean(selectedConversation && selectedDraft
    && (selectedDraft.rule !== selectedConversation.rule || selectedDraft.clientId !== selectedConversation.clientId));
  const selectedNeedsClient = selectedDraft?.rule === "SCOPED_TEAM_REVIEW" && !selectedDraft.clientId;
  const selectedSaving = selectedConversation?.conversationId === savingId;

  return (
    <section className="whatsapp-rules-panel" aria-labelledby="review-access-title">
      <header className="whatsapp-rules-panel__header">
        <span><ShieldCheck size={20} /></span>
        <div>
          <p className="eyebrow">Review access</p>
          <h2 id="review-access-title">Choose where Ops may prepare work and replies.</h2>
          <p>All WhatsApp chats are already monitored. This only chooses where suggestions may appear in Review.</p>
        </div>
        <span className="review-access-count">{conversations ? `${enabledConversations.length} Review ${enabledConversations.length === 1 ? "chat" : "chats"}` : "Loading chats"}</span>
      </header>
      <div className="whatsapp-rules-panel__body">
        {error ? <div className="whatsapp-rules-error" role="alert"><span>{error}</span><button className="secondary-button" type="button" onClick={() => void load()}>Try again</button></div> : null}
        {loading && conversations === null ? (
          <div className="whatsapp-rules-state"><LoaderCircle className="spin" size={22} /><span>Loading chats…</span></div>
        ) : editableConversations.length ? (
          <>
            <div className={selectedConversation ? "review-access-form has-selection" : "review-access-form"}>
              <label className="review-access-field review-access-field--chat">
                <span>Chat</span>
                <select value={selectedId} onChange={(event) => setSelectedId(event.target.value)} disabled={loading || Boolean(savingId)}>
                  <option value="">Choose a staff chat</option>
                  {(["GROUP", "DIRECT"] as const).map((kind) => {
                    const rows = editableConversations.filter((conversation) => conversation.kind === kind);
                    return rows.length ? (
                      <optgroup key={kind} label={kind === "GROUP" ? "Groups" : "Direct chats"}>
                        {rows.map((conversation) => (
                          <option key={conversation.conversationId} value={conversation.conversationId}>
                            {conversation.label} · {messageSummary(conversation.messageCount)}
                          </option>
                        ))}
                      </optgroup>
                    ) : null;
                  })}
                </select>
                {!selectedConversation ? <small>Nothing is selected automatically.</small> : null}
              </label>

              {selectedConversation && selectedDraft ? (
                <>
                  <label className="review-access-field">
                    <span>Allow Review suggestions for</span>
                    <select
                      value={selectedDraft.rule}
                      disabled={Boolean(selectedSaving)}
                      onChange={(event) => {
                        const rule = event.target.value as ConversationRule;
                        setDrafts((current) => ({
                          ...current,
                          [selectedConversation.conversationId]: {
                            rule,
                            clientId: rule === "SCOPED_TEAM_REVIEW" ? selectedDraft.clientId : null
                          }
                        }));
                      }}
                    >
                      {conversationRuleOptions(selectedConversation.kind).map((option) => (
                        <option key={option.rule} value={option.rule} disabled={option.rule === "SCOPED_TEAM_REVIEW" && clients.length === 0}>{option.label}</option>
                      ))}
                    </select>
                  </label>

                  {selectedDraft.rule === "SCOPED_TEAM_REVIEW" ? (
                    <label className="review-access-field">
                      <span>Client</span>
                      <select
                        value={selectedDraft.clientId ?? ""}
                        disabled={Boolean(selectedSaving)}
                        onChange={(event) => setDrafts((current) => ({
                          ...current,
                          [selectedConversation.conversationId]: { ...selectedDraft, clientId: event.target.value || null }
                        }))}
                      >
                        <option value="">Choose client</option>
                        {clients.map((client) => <option key={client.id} value={client.id}>{client.name}</option>)}
                      </select>
                    </label>
                  ) : null}

                  <button className="primary-button review-access-save" type="button" disabled={!selectedChanged || selectedNeedsClient || Boolean(selectedSaving)} onClick={() => void save(selectedConversation, selectedDraft)}>
                    {selectedSaving ? <LoaderCircle className="spin" size={16} /> : <ShieldCheck size={16} />}{selectedSaving ? "Saving" : "Save access"}
                  </button>
                </>
              ) : null}
            </div>

            {enabledConversations.length ? (
              <div className="review-access-active">
                <header><strong>Active Review chats</strong><span>{enabledConversations.length}</span></header>
                {enabledConversations.map((conversation) => (
                  <article key={conversation.conversationId}>
                    <div>
                      <strong>{conversation.label}</strong>
                      <small>{conversationKindLabel(conversation.kind)} · {conversationRuleLabel(conversation.kind, conversation.rule)}{conversation.clientId ? ` · ${clients.find((client) => client.id === conversation.clientId)?.name ?? "Client"}` : ""}</small>
                    </div>
                    <button className="secondary-button" type="button" onClick={() => setSelectedId(conversation.conversationId)}>Edit</button>
                  </article>
                ))}
              </div>
            ) : (
              <div className="review-access-empty"><CheckCircle2 size={18} /><span>Monitoring and search are live. No chat can create Review suggestions yet.</span></div>
            )}
          </>
        ) : (
          <div className="whatsapp-rules-state"><MessageCircleMore size={22} /><span>No staff chats with messages yet.</span></div>
        )}

        <footer><ShieldCheck size={16} /><span>Choosing a chat cannot send anything. Every suggestion still waits for approval in Review.</span></footer>
      </div>
    </section>
  );
}

export function conversationRuleOptions(kind: WhatsAppConversationRule["kind"]): readonly { rule: ConversationRule; label: string }[] {
  if (kind === "BROADCAST") return [{ rule: "READ_ONLY", label: "Monitor only" }];
  return [
    { rule: "READ_ONLY", label: "Monitor only" },
    { rule: "INTERNAL_REVIEW", label: "Internal operations" },
    { rule: "SCOPED_TEAM_REVIEW", label: "One client team" },
    ...(kind === "DIRECT" ? [{ rule: "OWNER_REVIEW" as const, label: "Private owner chat" }] : [])
  ];
}

export function conversationRuleLabel(kind: WhatsAppConversationRule["kind"], rule: ConversationRule): string {
  return conversationRuleOptions(kind).find((option) => option.rule === rule)?.label ?? "Monitor only";
}

function conversationKindLabel(kind: WhatsAppConversationRule["kind"]): string {
  if (kind === "GROUP") return "Group";
  if (kind === "DIRECT") return "Direct chat";
  return "Broadcast";
}

export function ConnectedAccountSummary({ connection, capture }: {
  connection: WhatsAppConnection;
  capture: WhatsAppCaptureSummary | null;
}) {
  return (
    <section className="whatsapp-connected-bar" aria-label="WhatsApp connection status">
      <span className="whatsapp-connected-bar__icon"><CheckCircle2 size={22} /></span>
      <div className="whatsapp-connected-bar__identity">
        <strong>WhatsApp transport connected</strong>
        <small>{liveCaptureSummaryText(connection)}</small>
      </div>
      <p className="whatsapp-connected-bar__activity">{captureSummaryText(capture)}</p>
      <span className={connection.observeOnly ? "whatsapp-connected-bar__mode is-locked" : "whatsapp-connected-bar__mode"}>
        <ShieldCheck size={16} />{connection.observeOnly ? "Sending locked" : "Approval required"}
      </span>
    </section>
  );
}

export function liveCaptureSummaryText(
  connection: Pick<WhatsAppConnection, "status" | "liveCaptureStatus" | "lastLiveBatchAt" | "completeThrough">
): string {
  if (connection.status !== "CONNECTED" || connection.liveCaptureStatus === "NOT_CONNECTED") {
    return "Live capture is not connected.";
  }
  if (connection.liveCaptureStatus !== "PROVEN"
    || !connection.completeThrough) {
    return "Live capture is connected, but coverage is not proven yet.";
  }
  const formatInstant = (value: string): string => {
    const instant = new Date(value);
    return Number.isFinite(instant.getTime())
      ? new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" }).format(instant)
      : value;
  };
  const coveredThrough = formatInstant(connection.completeThrough);
  if (!connection.lastLiveBatchAt || connection.lastLiveBatchAt === connection.completeThrough) {
    return `Message coverage proven through ${coveredThrough}.`;
  }
  return `Message coverage proven through ${coveredThrough}; last live batch ${formatInstant(connection.lastLiveBatchAt)}.`;
}

export function captureSummaryText(
  capture: Pick<WhatsAppCaptureSummary, "groupCount" | "directCount" | "messageCount"> | null
): string {
  if (!capture) return "Loading captured activity…";
  return `${capture.groupCount} ${capture.groupCount === 1 ? "group" : "groups"} · ${capture.directCount} ${capture.directCount === 1 ? "direct chat" : "direct chats"} · ${capture.messageCount} ${capture.messageCount === 1 ? "message" : "messages"}`;
}

function formatSearchTime(value: string): string {
  return new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

function PeopleMappingPanel(props: {
  people: WhatsAppPerson[] | null;
  members: BoardSnapshot["members"];
  error: string;
  loading: boolean;
  notify: (text: string, tone?: "success" | "error") => void;
  onRetry: () => void;
  onPersonChanged: (person: WhatsAppPerson) => void;
}) {
  const [selections, setSelections] = useState<Record<string, string>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const usedMemberIds = useMemo(
    () => new Set((props.people ?? []).flatMap((person) => person.memberId ? [person.memberId] : [])),
    [props.people]
  );
  const matchedCount = (props.people ?? []).filter((person) => person.memberId).length;

  const save = async (person: WhatsAppPerson, memberId: string) => {
    if (!memberId) return;
    setSavingId(person.id);
    try {
      const { person: changed } = await api.linkWhatsAppPerson(person.id, memberId);
      props.onPersonChanged(changed);
      const memberName = props.members.find((member) => member.id === memberId)?.displayName ?? "that team member";
      props.notify(`${person.displayName ?? "WhatsApp person"} is matched to ${memberName}.`);
    } catch (error) {
      props.notify(error instanceof Error ? error.message : "That match could not be saved.", "error");
    } finally {
      setSavingId(null);
    }
  };

  const remove = async (person: WhatsAppPerson) => {
    setSavingId(person.id);
    try {
      const { person: changed } = await api.unlinkWhatsAppPerson(person.id);
      props.onPersonChanged(changed);
      props.notify(`Match removed for ${person.displayName ?? "that WhatsApp person"}.`);
    } catch (error) {
      props.notify(error instanceof Error ? error.message : "That match could not be removed.", "error");
    } finally {
      setSavingId(null);
    }
  };

  return (
    <details className="people-card">
      <summary className="people-card__summary">
        <span className="people-card__icon"><UsersRound size={23} /></span>
        <div>
          <strong>Team matching</strong>
          <small>Link WhatsApp names to board members when needed.</small>
        </div>
        <span className="people-card__count">{props.people?.length ? `${matchedCount} of ${props.people.length} matched` : props.loading ? "Loading" : "No people yet"}</span>
        <ChevronDown className="disclosure-chevron" size={18} />
      </summary>

      <div className="people-card__body" aria-live="polite">
        {props.error && props.people ? (
          <div className="people-inline-error" role="alert">
            <CircleAlert size={18} />
            <span>{props.error}</span>
            <button className="secondary-button" type="button" onClick={props.onRetry}>Try again</button>
          </div>
        ) : null}

        {props.loading && props.people === null ? (
          <div className="people-card__state"><LoaderCircle className="spin" size={25} /><strong>Loading team matching…</strong></div>
        ) : props.error && props.people === null ? (
          <div className="people-card__state people-card__state--error" role="alert">
            <CircleAlert size={25} />
            <strong>{props.error}</strong>
            <button className="secondary-button" type="button" onClick={props.onRetry}><RefreshCw size={17} />Try again</button>
          </div>
        ) : props.people?.length ? (
          <div className="people-list">
            {props.people.map((person) => {
              const availableMembers = props.members.filter((member) => !usedMemberIds.has(member.id));
              const suggestedId = suggestMemberId(person.displayName, availableMembers);
              const chosenId = selections[person.id] && !usedMemberIds.has(selections[person.id]!)
                ? selections[person.id]!
                : suggestedId ?? "";
              const saving = savingId === person.id;
              const matchedMember = person.memberId ? props.members.find((member) => member.id === person.memberId) : null;
              const matchedLabel = matchedMember ? memberLabel(props.members, matchedMember) : person.memberDisplayName ?? "Team member";
              return (
                <article className={person.memberId ? "person-row is-matched" : "person-row"} key={person.id}>
                  <div className="person-row__identity">
                    <span className="person-avatar" aria-hidden="true">{personInitial(person.displayName)}</span>
                    <div>
                      <strong>{person.displayName ?? "Name unavailable"}</strong>
                      <small>{messageSummary(person.messageCount)} · {formatLastSeen(person.lastSeenAt)}</small>
                    </div>
                  </div>

                  {person.memberId ? (
                    <div className="person-row__match">
                      <span>Matched to</span>
                      <strong><CheckCircle2 size={17} />{matchedLabel}</strong>
                    </div>
                  ) : (
                    <label className="person-row__select">
                      <span>Team member</span>
                      <select
                        value={chosenId}
                        onChange={(event) => setSelections((current) => ({ ...current, [person.id]: event.target.value }))}
                        disabled={saving}
                      >
                        <option value="">Choose a team member</option>
                        {props.members.map((member) => (
                          <option key={member.id} value={member.id} disabled={usedMemberIds.has(member.id)}>
                            {memberLabel(props.members, member)}{usedMemberIds.has(member.id) ? " · already matched" : ""}
                          </option>
                        ))}
                      </select>
                      {suggestedId && chosenId === suggestedId ? <small>Same name found. Confirm before saving.</small> : null}
                    </label>
                  )}

                  <div className="person-row__action">
                    {person.memberId ? (
                      <button className="secondary-button" type="button" disabled={saving} onClick={() => void remove(person)}>
                        {saving ? <><LoaderCircle className="spin" size={17} />Removing…</> : "Remove match"}
                      </button>
                    ) : (
                      <button className="primary-button" type="button" disabled={!chosenId || saving} onClick={() => void save(person, chosenId)}>
                        {saving ? <><LoaderCircle className="spin" size={17} />Saving…</> : "Save match"}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="people-card__state">
            <MessageCircleMore size={26} />
            <strong>Waiting for the first new message.</strong>
            <p>Team members appear here after they speak in any captured conversation.</p>
          </div>
        )}
      </div>

      <footer className="people-card__footer">
        <ShieldCheck size={17} />
        <span>A WhatsApp name can change. Confirm the right team member before saving. The WhatsApp ID stays private.</span>
      </footer>
    </details>
  );
}

export function suggestMemberId(
  displayName: string | null,
  members: ReadonlyArray<Pick<BoardSnapshot["members"][number], "id" | "displayName">>
): string | null {
  // WHY [BOL-PEOPLE-UI-002]: pushName is self-asserted. An exact, unique name
  // may reduce setup typing, but this function only proposes a selection; the
  // owner must still press Save match before any identity gains authority.
  if (!displayName) return null;
  const normalized = displayName.trim().replace(/\s+/g, " ").toLocaleLowerCase();
  const matches = members.filter((member) => member.displayName.trim().replace(/\s+/g, " ").toLocaleLowerCase() === normalized);
  return matches.length === 1 ? matches[0]!.id : null;
}

export function formatLastSeen(value: string, now = Date.now()): string {
  const seenAt = Date.parse(value);
  if (!Number.isFinite(seenAt)) return "Seen recently";
  const elapsed = Math.max(0, now - seenAt);
  if (elapsed < 60_000) return "Seen just now";
  if (elapsed < 3_600_000) return `Seen ${Math.floor(elapsed / 60_000)} min ago`;
  if (elapsed < 86_400_000) return `Seen ${Math.floor(elapsed / 3_600_000)} hr ago`;
  if (elapsed < 604_800_000) {
    const days = Math.floor(elapsed / 86_400_000);
    return `Seen ${days} ${days === 1 ? "day" : "days"} ago`;
  }
  return `Last seen ${new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(new Date(seenAt))}`;
}

function personInitial(displayName: string | null): string {
  return displayName?.trim().charAt(0).toLocaleUpperCase() || "?";
}

function messageSummary(count: number): string {
  return count === 1 ? "1 message" : `${count} messages`;
}

export function connectionState(status: WhatsAppConnection["status"]): {
  label: string;
  shortLabel: string;
  heading: string;
  copy: string;
  tone: "ready" | "waiting" | "error";
  icon: typeof CheckCircle2;
  spinning?: boolean;
} {
  if (status === "CONNECTED") return {
    label: "Connected",
    shortLabel: "Live",
    heading: "The business number is connected.",
    copy: "Every available group and direct chat is monitored automatically.",
    tone: "ready",
    icon: CheckCircle2
  };
  if (status === "SCAN_REQUIRED") return {
    label: "Phone needed",
    shortLabel: "Scan now",
    heading: "Scan the QR code.",
    copy: "Use the business phone to link this bot.",
    tone: "waiting",
    icon: Smartphone
  };
  if (status === "DISCONNECTED") return {
    label: "Disconnected",
    shortLabel: "Offline",
    heading: "WhatsApp is disconnected.",
    copy: "Reconnect now. If WhatsApp needs the phone again, a QR code will appear here.",
    tone: "error",
    icon: WifiOff
  };
  if (status === "UNAVAILABLE") return {
    label: "Connector offline",
    shortLabel: "Unavailable",
    heading: "The WhatsApp connector is offline.",
    copy: "Start the private connector service, then return to this page.",
    tone: "error",
    icon: CircleAlert
  };
  return {
    label: status === "RECONNECTING" ? "Reconnecting" : "Connecting",
    shortLabel: "Please wait",
    heading: status === "RECONNECTING" ? "Reconnecting WhatsApp…" : "Connecting to WhatsApp…",
    copy: "This page will show the next step automatically.",
    tone: "waiting",
    icon: LoaderCircle,
    spinning: true
  };
}

export function connectionStatusForView(
  connection: WhatsAppConnection | null,
  unavailable: boolean
): WhatsAppConnection["status"] {
  return connection?.status ?? (unavailable ? "UNAVAILABLE" : "STARTING");
}
